import { Button, Dialog, DropdownMenu } from '@cloudflare/kumo';
import { List, SignOut, X } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { api, chatsQuery, meQuery, queryClient, sitesQuery, unwrap } from '../api.ts';
import wirechunkLogo from '../assets/wirechunk-logo.svg';
import { ErrorNotice, Loading } from '../common.tsx';
import { ChatSidebar } from './chat.tsx';
import { NewSite } from './new-site.tsx';
import { UnpublishedChanges } from './unpublished-changes.tsx';
import { Workbench } from './workbench.tsx';
export function ProtectedLayout() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const pathname = useRouterState({ select: (state) => state.location.pathname.replace(/\/$/, '') || '/' });
  const inWorkbench = pathname === '/' || pathname.startsWith('/sites/');
  const [view, setView] = useState<{ path: string; chatId?: string; tab: 'chat' | 'site' }>({
    path: pathname,
    chatId: search.chatId,
    tab: pathname.startsWith('/sites/') ? 'site' : 'chat',
  });
  if (view.path !== pathname || view.chatId !== search.chatId) {
    setView({
      path: pathname,
      chatId: search.chatId,
      tab: view.chatId !== search.chatId || !pathname.startsWith('/sites/') ? 'chat' : 'site',
    });
  }
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [draft, setDraft] = useState(0);
  const newChat = () => {
    setSidebarOpen(false);
    setDraft((value) => value + 1);
    setView({ ...view, tab: 'chat' });
    void navigate({ to: inWorkbench ? '.' : '/', search: (prev) => ({ ...prev, chatId: undefined }) });
  };
  const sidebarProps = {
    to: inWorkbench ? ('.' as const) : ('/' as const),
    activeId: search.chatId,
    onNew: newChat,
    onSelect: () => {
      setSidebarOpen(false);
      setView({ ...view, tab: 'chat' });
    },
  };
  const me = useQuery(meQuery);
  const home = pathname === '/';
  const sites = useQuery({ ...sitesQuery, enabled: home && me.data?.emailVerified === true });
  const chats = useQuery({ ...chatsQuery, enabled: home && me.data?.emailVerified === true });
  const [homeMode, setHomeMode] = useState<'new-site' | 'dashboard'>();
  // Keep the creation form open while its first message and attachments are being sent.
  // Choose the home screen again on the next visit.
  if (!home && homeMode !== undefined) setHomeMode(undefined);
  if (home && search.chatId && homeMode !== 'dashboard') setHomeMode('dashboard');
  else if (home && homeMode === undefined) {
    if (sites.isError || chats.isError) setHomeMode('dashboard');
    else if (sites.isSuccess && chats.isSuccess && !sites.isFetching && !chats.isFetching) {
      setHomeMode(sites.data.length || chats.data.length ? 'dashboard' : 'new-site');
    }
  }
  const loadingHome = home && homeMode === undefined;
  const creatingFirstSite = home && homeMode === 'new-site';
  const showSidebar = pathname !== '/new' && !creatingFirstSite && !loadingHome;
  const signOut = useMutation({
    mutationFn: async () => unwrap(await api.POST('/api/auth/sign-out')),
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: '/sign-in' });
    },
  });
  const resend = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/auth/send-verification-email', {
          body: { email: me.data!.email },
        }),
      ),
  });
  if (me.isPending) return <Loading />;
  if (me.error && 'status' in me.error && me.error.status === 401) return <SignInRequired />;
  if (me.error) return <ErrorNotice error={me.error} />;
  const verified = me.data.emailVerified === true;
  return (
    <>
      <header className="app-toolbar">
        {verified && showSidebar && (
          <Button
            className="sidebar-toggle"
            variant="ghost"
            shape="square"
            aria-label="Open chats"
            onClick={() => setSidebarOpen(true)}
          >
            <List size={22} />
          </Button>
        )}
        <Link to="/" className="shrink-0" aria-label="Wirechunk home">
          <img className="block h-6" src={wirechunkLogo} alt="Wirechunk" />
        </Link>
        <div className="ml-auto min-w-0">{verified && <UnpublishedChanges />}</div>
        <div className="col-start-2 row-start-1">
          <AccountMenu
            name={me.data.name}
            verified={verified}
            signingOut={signOut.isPending}
            onSignOut={() => signOut.mutate()}
          />
        </div>
      </header>
      <ErrorNotice error={signOut.error} />
      {verified ? (
        <div className="app-body">
          {showSidebar && (
            <>
              <aside className="app-sidebar">
                <ChatSidebar {...sidebarProps} compact />
              </aside>
              <Dialog.Root open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <Dialog
                  className="!fixed !inset-y-0 !left-0 !right-auto !m-0 !h-dvh !max-h-none !overflow-y-auto !w-72 !max-w-[85vw] !translate-x-0 !translate-y-0 !rounded-none !p-0"
                  aria-describedby={undefined}
                >
                  <div className="flex items-center justify-between px-5 pt-4">
                    <Dialog.Title>Chats</Dialog.Title>
                    <Button
                      variant="ghost"
                      shape="square"
                      aria-label="Close chats"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <X size={18} />
                    </Button>
                  </div>
                  <ChatSidebar {...sidebarProps} />
                </Dialog>
              </Dialog.Root>
            </>
          )}
          <div className="app-content">
            {loadingHome ? (
              <Loading />
            ) : creatingFirstSite ? (
              <NewSite onDashboard={() => setHomeMode('dashboard')} />
            ) : inWorkbench ? (
              <Workbench draft={draft} view={view.tab} onViewChange={(tab) => setView({ ...view, tab })} />
            ) : (
              <Outlet />
            )}
          </div>
        </div>
      ) : (
        <main className="mx-auto mt-[clamp(56px,10vh,104px)] mb-12 w-full max-w-[528px] px-6">
          <h1 className="mb-4 text-[28px] leading-[1.2] font-semibold tracking-[-0.025em]">
            Verify your email
          </h1>
          <p className="text-[15px] leading-[1.65] text-kumo-subtle">
            Please click the link we sent to{' '}
            <strong className="font-medium text-kumo-default wrap-anywhere">{me.data.email}</strong>
          </p>
          <div className="grid gap-3 mt-7">
            <ErrorNotice error={resend.error} />
            <div>
              <Button
                className="-mx-2"
                variant="ghost"
                size="sm"
                onClick={() => resend.mutate()}
                disabled={resend.isPending}
              >
                {resend.isPending ? 'Sending…' : 'Resend verification email'}
              </Button>
            </div>
            {resend.isSuccess && (
              <p className="leading-[1.65] text-[14px] text-kumo-subtle" role="status">
                Verification email sent.
              </p>
            )}
          </div>
          {import.meta.env.DEV && (
            <a className="inline-block mt-6" href="/__dev/mail" target="_blank" rel="noreferrer">
              Open development mailbox
            </a>
          )}
        </main>
      )}
    </>
  );
}
function firstInitial(name: string) {
  const letter = [...name.trim()][0];
  return letter ? letter.toLocaleUpperCase() : '?';
}
function AccountItems({
  name,
  verified,
  signingOut,
  onSignOut,
}: {
  name: string;
  verified: boolean;
  signingOut: boolean;
  onSignOut: () => void;
}) {
  return (
    <>
      <DropdownMenu.Group>
        <DropdownMenu.Label>{name}</DropdownMenu.Label>
      </DropdownMenu.Group>
      <DropdownMenu.Separator />
      {verified && (
        <>
          <DropdownMenu.LinkItem href="/connections" closeOnClick render={<Link to="/connections" />}>
            Connected agents
          </DropdownMenu.LinkItem>
          <DropdownMenu.LinkItem href="/billing" closeOnClick render={<Link to="/billing" />}>
            Billing
          </DropdownMenu.LinkItem>
        </>
      )}
      <DropdownMenu.Item icon={SignOut} disabled={signingOut} onClick={onSignOut}>
        Sign out
      </DropdownMenu.Item>
    </>
  );
}
function AccountMenu({
  name,
  verified,
  signingOut,
  onSignOut,
}: {
  name: string;
  verified: boolean;
  signingOut: boolean;
  onSignOut: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger
        className="p-0 border-0 border-current w-9 h-9 rounded-full bg-kumo-contrast text-kumo-inverse text-[14px] font-semibold grid place-items-center leading-[1] [&:hover]:bg-kumo-contrast/90 [&[data-popup-open]]:bg-kumo-contrast/90"
        aria-label="Account menu"
      >
        {firstInitial(name)}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        <AccountItems name={name} verified={verified} signingOut={signingOut} onSignOut={onSignOut} />
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
function SignInRequired() {
  // Keep the destination stable while the router transitions away from this layout.
  const [returnTo] = useState(() => `${location.pathname}${location.search}${location.hash}`);
  return <Navigate to="/sign-in" search={{ returnTo }} />;
}
